import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import EmployerLogo from "../components/EmployerLogo";
import {
  Building2,
  ExternalLink,
  ArrowRight,
  Globe,
  Star,
  MapPin,
  ArrowUpRight,
} from "lucide-react";

const SITE_URL = "https://railwaycareers.co.uk";

function formatSalary(v) {
  if (v.salary_min && v.salary_max) {
    return `£${v.salary_min.toLocaleString()}–£${v.salary_max.toLocaleString()}`;
  }

  if (v.salary_min) {
    return `From £${v.salary_min.toLocaleString()}`;
  }

  return "Competitive";
}

export default function CompanyProfile() {
  const { slug } = useParams();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);

    api
      .get(`/companies/${slug}`)
      .then((response) => setProfile(response.data))
      .catch((error) => {
        if (error.response?.status === 404) {
          setNotFound(true);
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useSeo({
    title: profile
      ? `${profile.company_name} · Careers on Railway Careers UK`
      : "Company profile · Railway Careers UK",

    description:
      profile?.profile_tagline ||
      profile?.profile_about?.slice(0, 160) ||
      "Live UK rail vacancies and careers information.",

    canonical: `/companies/${slug}`,

    jsonLd:
      profile && {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: profile.company_name,
        url: `${SITE_URL}/companies/${slug}`,
        logo: profile.profile_logo_url || undefined,
        description: profile.profile_about || undefined,
        sameAs: [
          profile.profile_website,
          profile.profile_careers_url,
        ].filter(Boolean),
      },
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-muted-foreground text-sm">
          Loading company page…
        </div>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Building2 className="w-12 h-12 text-muted-foreground mx-auto" />

          <h1 className="font-display font-bold text-ink text-2xl mt-4">
            Company page not available
          </h1>

          <p className="text-muted-foreground mt-2 text-sm">
            This employer hasn't published a public page yet.
          </p>

          <Button
            asChild
            className="mt-6 bg-navy text-bone hover:bg-lime-600 hover:text-ink"
          >
            <Link to="/operators">
              Browse all UK rail employers
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const {
    company_name,
    profile_tagline,
    profile_about,
    profile_hero_image_url,
    profile_logo_url,
    profile_website,
    profile_careers_url,
    featured_employer,
    vacancies = [],
  } = profile;

  return (
    <div
      className="bg-cream min-h-screen"
      data-testid="company-profile-page"
    >
      <section
        className="bg-navy relative overflow-hidden"
        style={
          profile_hero_image_url
            ? {
                backgroundImage: `linear-gradient(rgba(10,22,40,0.84), rgba(10,22,40,0.88)), url('${profile_hero_image_url}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg bg-white/95 border border-white/20 overflow-hidden flex items-center justify-center shrink-0 shadow-sm">
              {profile_logo_url ? (
                <img
                  src={profile_logo_url}
                  alt={`${company_name} logo`}
                  className="w-full h-full object-contain p-2"
                />
              ) : (
                <EmployerLogo
                  name={company_name}
                  size={96}
                />
              )}
            </div>

            <div className="flex-1 min-w-[260px]">
              {featured_employer && (
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-amber font-bold mb-2">
                  <Star className="w-3 h-3 fill-amber" />
                  Featured employer
                </div>
              )}

              <h1
                className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.1] drop-shadow-sm"
                style={{ color: "#F2F7FF" }}
              >
                {company_name}
              </h1>

              {profile_tagline && (
                <p
                  className="mt-3 text-lg md:text-xl leading-relaxed max-w-3xl drop-shadow-sm"
                  style={{
                    color: "rgba(242,247,255,0.82)",
                  }}
                >
                  {profile_tagline}
                </p>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                {profile_careers_url && (
                  <Button
                    asChild
                    className="bg-amber text-ink hover:bg-lime-600 hover:text-ink font-semibold"
                  >
                    <a
                      href={profile_careers_url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      data-testid="company-careers-link"
                    >
                      Visit careers page
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                )}

                {profile_website && (
                  <Button
                    asChild
                    variant="outline"
                    className="border-amber text-amber hover:bg-amber hover:text-ink"
                  >
                    <a
                      href={profile_website}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                    >
                      <Globe className="mr-2 w-4 h-4" />
                      Website
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16 grid md:grid-cols-[1fr_2fr] gap-10">
        {profile_about && (
          <aside className="md:sticky md:top-24 md:self-start">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink font-bold">
              About {company_name}
            </div>

            <div className="mt-3 text-muted-foreground leading-relaxed text-[15px] whitespace-pre-line">
              {profile_about}
            </div>
          </aside>
        )}

        <section
          className={
            profile_about
              ? ""
              : "md:col-span-2"
          }
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display font-bold text-ink text-2xl md:text-3xl tracking-tight">
              Live vacancies at {company_name}
            </h2>

            <div className="text-xs text-muted-foreground font-mono">
              {vacancies.length} live
            </div>
          </div>

          {vacancies.length === 0 ? (
            <div className="mt-5 border border-border rounded-lg p-8 text-center text-muted-foreground">
              No live vacancies right now. Subscribe to the{" "}
              <Link
                to="/"
                className="underline text-ink font-semibold"
              >
                Monday Briefing
              </Link>{" "}
              to be notified when {company_name} posts next.
            </div>
          ) : (
            <div
              className="mt-6 space-y-3"
              data-testid="company-vacancies"
            >
              {vacancies.map((vacancy) => (
                <Link
                  key={vacancy.id}
                  to={`/vacancies/${vacancy.id}`}
                  className="block border border-border rounded-lg p-4 bg-card hover:border-amber transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-ink line-clamp-1 group-hover:text-amber-700">
                        {vacancy.featured && (
                          <Star className="inline w-3.5 h-3.5 fill-amber text-amber mr-1.5 align-middle" />
                        )}

                        {vacancy.title}
                      </div>

                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {vacancy.location}
                        </span>

                        <span className="font-mono">
                          {formatSalary(vacancy)}
                        </span>

                        <span className="capitalize">
                          {(
                            vacancy.employment_type ||
                            "full-time"
                          ).replace("-", " ")}
                        </span>
                      </div>
                    </div>

                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-700 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-8 border-t border-border pt-6 flex flex-wrap gap-3">
            <Button
              asChild
              variant="outline"
            >
              <Link to="/vacancies">
                Browse all UK rail vacancies
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
            >
              <Link to="/operators">
                All UK rail employers
              </Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}